// Enhancements of the content of posts and pages

import {onImageLoaded} from '../lib/dom.ts'

/** Fills a post's `.toc` list with links to its h2 headings, and with `data-show-children="true"` also to the h3 headings below each h2 */
export function populateToc() {
    const toc = document.querySelector<HTMLElement>('.article-content .toc')
    if (!toc) return

    document.querySelectorAll<HTMLHeadingElement>('.article-content h2[id]:not([id=""])').forEach(h2 => {
        toc.append(tocItem(h2))
        if (toc.dataset.showChildren !== 'true') return

        const h3s = headingsUntilNextH2(h2).filter(h3 => h3.id && h3.id !== 'sources')
        if (!h3s.length) return
        const indent = document.createElement('li')
        indent.className = 'toc-indent'
        const list = document.createElement('ul')
        list.append(...h3s.map(tocItem))
        indent.append(list)
        toc.append(indent)
    })
}

function tocItem(heading: HTMLHeadingElement) {
    const link = document.createElement('a')
    link.href = `#${heading.id}`
    link.textContent = heading.textContent
    const item = document.createElement('li')
    item.append(link)
    return item
}

/** Returns the h3 headings that follow the h2 until the next h2 */
function headingsUntilNextH2(h2: HTMLHeadingElement) {
    const h3s: HTMLHeadingElement[] = []
    for (let sibling = h2.nextElementSibling; sibling && sibling.tagName !== 'H2'; sibling = sibling.nextElementSibling) {
        if (sibling instanceof HTMLHeadingElement && sibling.tagName === 'H3') h3s.push(sibling)
    }
    return h3s
}

/** Styles Ghost's product cards that link to the Photone download page */
export function markDownloadProductCards() {
    document.querySelectorAll('.kg-product-card').forEach(card => {
        const href = card.querySelector<HTMLAnchorElement>('.kg-product-card-button')?.href
        if (href?.includes('growlightmeter.com/download')) card.classList.add('download-photone')
    })
}

/** Removes the shadow (and white background) of article images whose top left pixel is the page's grey or transparent, i.e. that have no edge */
export function removeShadowOfBorderlessImages() {
    document.querySelectorAll<HTMLImageElement>('.article-content .kg-image-card img').forEach(img => onImageLoaded(img, () => styleImageEdge(img)))
}

function styleImageEdge(img: HTMLImageElement) {
    let pixel: Uint8ClampedArray
    try {
        pixel = topLeftPixel(img)
    } catch {
        return // broken image, or a cross-origin one whose pixels can't be read
    }
    const [red, green, blue, alpha] = pixel
    const isPhotoneGrey = red === 247 && green === 247 && blue === 247
    if (isPhotoneGrey) {
        img.style.boxShadow = 'none'
    } else if (alpha === 0) {
        img.style.boxShadow = 'none'
        img.style.backgroundColor = 'transparent'
        img.style.backgroundImage = 'none'
    } else {
        img.style.boxShadow = 'auto'
        img.style.backgroundColor = 'auto'
        img.style.backgroundImage = 'auto'
    }
}

/** Returns the RGBA values of the image's top left pixel */
function topLeftPixel(img: HTMLImageElement) {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    const context = canvas.getContext('2d') as CanvasRenderingContext2D
    context.drawImage(img, 0, 0, 1, 1, 0, 0, 1, 1)
    return context.getImageData(0, 0, 1, 1).data
}

/** Removes the spinner behind images in `.img-wrapper--with-loader` once they're loaded */
export function removeLoadersOfLoadedImages() {
    document.querySelectorAll<HTMLImageElement>('.img-wrapper--with-loader img').forEach(img => {
        onImageLoaded(img, () => (img.closest('.img-wrapper--with-loader') as HTMLElement).style.background = 'none')
    })
}
